import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Search,
  Loader2,
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  BookOpen,
  Command as CommandIcon,
  MessageSquareText,
  Library,
} from "lucide-react";
import Reveal from "@/components/site/Reveal";
import PageSeo from "@/components/site/PageSeo";
import { SUPPORT } from "@/constants/testIds";
import { SERVICE_CATALOG } from "@/constants/content";
import { useWebsiteContent } from "@/hooks/useWebsiteContent";

const API = "/api";

export default function Support() {
  const { data: wc } = useWebsiteContent();
  const kbArticles   = wc?.kbArticles || [];
  const quickPrompts = (wc?.quickPrompts || []).map((qp) => ({ slug: qp.slug, q: qp.prompt }));
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [sources, setSources] = useState([]);
  const [related, setRelated] = useState(null);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [results, setResults] = useState(null); // server search results

  const answerRef = useRef(null);

  useEffect(() => {
    if (!loading && !answer && !error) return;
    const el = answerRef.current;
    if (!el) return;
    const t = window.setTimeout(() => {
      const supportsSmooth =
        typeof CSS !== "undefined" && CSS.supports && CSS.supports("scroll-behavior", "smooth");
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : supportsSmooth
        ? "smooth"
        : "auto";
      el.scrollIntoView({ behavior, block: "start" });
      if (answer || error) {
        el.focus({ preventScroll: true });
      }
    }, 60);
    return () => window.clearTimeout(t);
  }, [loading, answer, error]);

  // Server-side Related Content search (replaces the old client filter).
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults(null);
      return;
    }
    const ctrl = new AbortController();
    const t = window.setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/support/search`, {
          params: { q: term },
          signal: ctrl.signal,
        });
        setResults(r.data);
      } catch {
        /* keep previous results on abort/error */
      }
    }, 300);
    return () => {
      window.clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text) return;
    setLoading(true);
    setError(null);
    setAnswer(null);
    setSources([]);
    setRelated(null);
    try {
      const res = await axios.post(`${API}/support/ask`, {
        question: text,
        session_id: sessionId,
      });
      setAnswer(res.data.answer);
      setSources(res.data.sources || []);
      setRelated(res.data.related || null);
      setSessionId(res.data.session_id);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "We couldn't answer right now. Please try again or contact our team."
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    ask();
  };

  const searching = results !== null;
  const articleItems = searching ? results.articles || [] : [];
  const resourceItems = searching ? results.resources || [] : kbArticles;
  const serviceItems = searching
    ? (results.services || []).map((s) => ({ slug: s.slug, title: s.title }))
    : SERVICE_CATALOG.map((s) => ({ slug: s.slug, title: s.title }));

  return (
    <main data-testid={SUPPORT.root}>
      <PageSeo
        path="/support"
        title="Support — Answers from MTS knowledge"
        description="Ask a question about sourcing, manufacturing, quality control or trading. Our AI assistant answers only from MTS' own knowledge base — no outside sources."
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home",
                item: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
              { "@type": "ListItem", position: 2, name: "Support" },
            ],
          },
        ]}
      />
      {/* HEADER + BIG SEARCH ============================================= */}
      <section className="border-b border-[#ececef] mts-soft-gradient relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 mts-dot-grid opacity-40 [mask-image:radial-gradient(60%_60%_at_50%_40%,black,transparent)]"
        />
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-20 text-center">
          <Reveal delay={80}>
            <h1 className="text-[44px] sm:text-[60px] md:text-[80px] font-semibold tracking-[-0.03em] leading-[1.02] text-[#0a0a0a] max-w-3xl mx-auto text-balance">
              {wc?.supportTitle || ""}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 text-[16px] md:text-[18px] leading-relaxed text-[#424245] max-w-2xl mx-auto text-pretty">
              {wc?.supportSubtitle || ""}
            </p>
          </Reveal>

          {/* Big search shell */}
          <Reveal delay={220}>
            <form
              onSubmit={onSubmit}
              className="mts-search-shell mt-12 mx-auto max-w-2xl px-5 py-4 flex items-center gap-4"
            >
              <Search
                size={20}
                strokeWidth={1.5}
                className="text-[#86868b] shrink-0"
              />
              <input
                data-testid={SUPPORT.searchInput}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                type="text"
                placeholder=""
                className="flex-1 bg-transparent outline-none text-[16px] text-[#0a0a0a] placeholder:text-[#86868b] text-left"
              />
              <span className="hidden sm:inline-flex items-center gap-1">
                <span className="mts-kbd">
                  <CommandIcon size={11} strokeWidth={2} />
                </span>
                <span className="mts-kbd">K</span>
              </span>
              <button
                type="submit"
                data-testid={SUPPORT.askButton}
                disabled={loading || !q.trim()}
                className="mts-btn-primary py-2.5 px-4 text-[13px]"
              >
                {loading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    Go <ArrowRight size={14} />
                  </>
                )}
              </button>
            </form>
          </Reveal>

          {/* Quick prompts */}
          <Reveal delay={280}>
            <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-3xl mx-auto">
              {quickPrompts.map((p) => (
                <button
                  key={p.slug}
                  data-testid={SUPPORT.quickPrompt(p.slug)}
                  onClick={() => {
                    setQ(p.q);
                    ask(p.q);
                  }}
                  className="inline-flex items-center gap-1.5 text-[12.5px] text-[#424245] bg-white border border-[#e5e5ea] rounded-full px-3.5 py-1.5 hover:border-[#0a0a0a] hover:text-[#0a0a0a] hover:shadow-sm transition-all"
                  type="button"
                >
                  <Sparkles size={11} strokeWidth={1.75} className="opacity-60" />
                  {p.q}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ANSWER ========================================================== */}
      {(answer || error || loading) && (
        <section
          ref={answerRef}
          tabIndex={-1}
          aria-live="polite"
          aria-busy={loading ? "true" : "false"}
          className="border-b border-[#ececef] bg-white scroll-mt-24 focus:outline-none"
        >
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-12 md:py-16">
            <div className="max-w-3xl mx-auto">
              <div className="mts-card p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <div className="mts-icon-chip !h-9 !w-9 rounded-xl">
                    <MessageSquareText size={16} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                      Assistant
                    </p>
                    <p className="text-[13px] text-[#0a0a0a] font-medium">
                      Grounded on MTS Master Research
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  {loading && (
                    <div
                      className="flex items-center gap-2.5 text-[14.5px] text-[#6e6e73]"
                      role="status"
                      aria-label="Searching MTS knowledge base"
                    >
                      <Loader2 size={15} className="animate-spin" strokeWidth={1.75} />
                      <span>Searching MTS knowledge base…</span>
                    </div>
                  )}
                  {answer && (
                    <p
                      data-testid={SUPPORT.answer}
                      className="text-[15.5px] leading-[1.7] text-[#0a0a0a] whitespace-pre-wrap"
                    >
                      {answer}
                    </p>
                  )}
                  {error && (
                    <p className="text-[14.5px] leading-relaxed text-[#8a1c1c]">
                      {error}
                    </p>
                  )}
                </div>

                {/* Knowledge sources (Master Research chapters/sections) */}
                {sources.length > 0 && (
                  <div
                    data-testid="support-sources"
                    className="mt-5 flex flex-wrap gap-2"
                  >
                    {sources.map((s) => (
                      <span
                        key={s.section_no}
                        data-testid={`support-source-${s.section_no}`}
                        className="inline-flex items-center gap-1.5 text-[11.5px] text-[#424245] bg-[#f5f5f7] border border-[#e5e5ea] rounded-full px-3 py-1"
                        title={`Chapter ${s.chapter_no}: ${s.chapter_title}`}
                      >
                        <Library size={11} strokeWidth={1.75} className="opacity-60" />
                        {s.section_no} {s.section_title}
                      </span>
                    ))}
                  </div>
                )}

                {/* Related content (recommendations, not knowledge) */}
                {related &&
                  (related.articles?.length > 0 ||
                    related.services?.length > 0 ||
                    related.resources?.length > 0) && (
                  <div className="mt-6 pt-5 border-t border-[#ececef] grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {related.articles?.length > 0 && (
                      <div data-testid="support-related-articles">
                        <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b] mb-2">
                          Related Articles
                        </p>
                        <ul className="space-y-1.5">
                          {related.articles.slice(0, 4).map((a) => (
                            <li key={a.slug}>
                              <Link
                                to={`/blog/${a.slug}`}
                                className="text-[13px] text-[#0a0a0a] hover:opacity-70 leading-snug inline-block"
                              >
                                {a.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {related.services?.length > 0 && (
                      <div data-testid="support-related-services">
                        <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b] mb-2">
                          Related Services
                        </p>
                        <ul className="space-y-1.5">
                          {related.services.slice(0, 4).map((s) => (
                            <li key={s.slug}>
                              <Link
                                to={`/services#${s.slug}`}
                                className="text-[13px] text-[#0a0a0a] hover:opacity-70 leading-snug inline-block"
                              >
                                {s.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {related.resources?.length > 0 && (
                      <div data-testid="support-related-resources">
                        <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b] mb-2">
                          Related Resources
                        </p>
                        <ul className="space-y-1.5">
                          {related.resources.slice(0, 4).map((r) => (
                            <li key={r.slug}>
                              <button
                                type="button"
                                onClick={() => {
                                  setQ(r.title);
                                  ask(r.title);
                                }}
                                className="text-[13px] text-[#0a0a0a] hover:opacity-70 leading-snug text-left"
                              >
                                {r.title}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-8 pt-6 border-t border-[#ececef] flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[12px] text-[#86868b]">
                    Answers reflect the MTS Master Research knowledge base only.
                  </p>
                  <Link
                    to="/contact"
                    data-testid={SUPPORT.fallbackContact}
                    className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0a0a0a] hover:opacity-70"
                  >
                    Still need help? Contact our team{" "}
                    <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* KNOWLEDGE / SEARCH RESULTS ====================================== */}
      <section className="border-b border-[#ececef] bg-[#f7f7f8]">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#e5e5ea] bg-white px-3 py-1 text-[11px] text-[#424245]">
                <BookOpen size={12} strokeWidth={1.75} />
                {searching ? "Search results" : "Knowledge base"}
              </div>
              <h2 className="mt-6 text-[32px] md:text-[44px] font-semibold tracking-[-0.03em] text-[#0a0a0a] leading-[1.05]">
                {searching ? (
                  <>
                    Matching{" "}
                    <span
                      className="italic font-normal"
                      style={{ fontFamily: "'Instrument Serif', serif" }}
                    >
                      materials.
                    </span>
                  </>
                ) : (
                  <>
                    Articles &amp;{" "}
                    <span
                      className="italic font-normal"
                      style={{ fontFamily: "'Instrument Serif', serif" }}
                    >
                      quick answers.
                    </span>
                  </>
                )}
              </h2>
              <p className="mt-4 text-[14px] leading-relaxed text-[#6e6e73] max-w-sm">
                {searching
                  ? "Published articles and resources matching your search."
                  : "Focused answers to the questions decision makers ask before starting a project."}
              </p>
            </div>
            <div className="md:col-span-8">
              <ul className="space-y-4" data-testid="support-kb-list">
                {searching && articleItems.length === 0 && resourceItems.length === 0 && (
                  <li className="mts-card p-6 text-[14px] text-[#6e6e73]">
                    No materials match. Try the assistant above or{" "}
                    <Link to="/contact" className="underline text-[#0a0a0a]">
                      contact our team
                    </Link>
                    .
                  </li>
                )}
                {/* Blog articles from server search — link to the article */}
                {articleItems.map((a) => (
                  <li key={`art-${a.slug}`}>
                    <Link
                      to={`/blog/${a.slug}`}
                      data-testid={`support-result-article-${a.slug}`}
                      className="mts-card block w-full text-left p-6 md:p-7 group"
                    >
                      <div className="flex items-start justify-between gap-6">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b] mb-1.5">
                            Blog article
                          </p>
                          <h3 className="text-[16px] md:text-[17px] font-medium text-[#0a0a0a] group-hover:opacity-80 tracking-tight">
                            {a.title}
                          </h3>
                          <p className="mt-2 text-[14px] leading-relaxed text-[#6e6e73]">
                            {a.excerpt}
                          </p>
                        </div>
                        <ArrowUpRight
                          size={16}
                          className="text-[#86868b] mt-1 shrink-0 group-hover:text-[#0a0a0a] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        />
                      </div>
                    </Link>
                  </li>
                ))}
                {/* FAQ resources — ask the assistant */}
                {resourceItems.map((a) => (
                  <li key={`faq-${a.slug}`}>
                    <button
                      type="button"
                      data-testid={`support-result-resource-${a.slug}`}
                      onClick={() => {
                        setQ(a.title);
                        ask(a.title);
                      }}
                      className="mts-card w-full text-left p-6 md:p-7 group"
                    >
                      <div className="flex items-start justify-between gap-6">
                        <div>
                          {searching && (
                            <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b] mb-1.5">
                              Quick answer
                            </p>
                          )}
                          <h3 className="text-[16px] md:text-[17px] font-medium text-[#0a0a0a] group-hover:opacity-80 tracking-tight">
                            {a.title}
                          </h3>
                          <p className="mt-2 text-[14px] leading-relaxed text-[#6e6e73]">
                            {a.excerpt}
                          </p>
                        </div>
                        <ArrowUpRight
                          size={16}
                          className="text-[#86868b] mt-1 shrink-0 group-hover:text-[#0a0a0a] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES ======================================================== */}
      <section className="border-b border-[#ececef] bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
                Services
              </p>
              <h2 className="mt-6 text-[32px] md:text-[44px] font-semibold tracking-[-0.03em] text-[#0a0a0a] leading-[1.05]">
                Jump directly to what{" "}
                <span
                  className="italic font-normal"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  you need.
                </span>
              </h2>
            </div>
            <div className="md:col-span-8">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2" data-testid="support-services-list">
                {serviceItems.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/services#${s.slug}`}
                      data-testid={`support-result-service-${s.slug}`}
                      className="flex items-center justify-between gap-4 border-b border-[#ececef] py-4 hover:border-[#0a0a0a] transition-colors"
                    >
                      <p className="text-[15px] font-medium text-[#0a0a0a] tracking-tight">
                        {s.title}
                      </p>
                      <ArrowUpRight size={16} className="text-[#86868b]" />
                    </Link>
                  </li>
                ))}
                {serviceItems.length === 0 && (
                  <li className="text-[14px] text-[#6e6e73] py-4">
                    No services match your search.
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA — dark ====================================================== */}
      <section className="relative overflow-hidden">
        <div className="mts-dark-panel">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
              <div className="md:col-span-8">
                <h2 className="text-[36px] sm:text-[48px] md:text-[64px] font-semibold tracking-[-0.03em] text-white leading-[1.05] max-w-2xl">
                  Didn&apos;t find your{" "}
                  <span
                    className="italic font-normal text-white/90"
                    style={{ fontFamily: "'Instrument Serif', serif" }}
                  >
                    answer?
                  </span>
                </h2>
                <p className="mt-6 text-[16px] leading-relaxed text-white/70 max-w-xl">
                  Send us a note — we reply within one business day.
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link
                  to="/contact"
                  className="mts-btn-inverse"
                >
                  Contact Us <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
